import "#/legacy/namespaces.js";
import "#legacy/admin/components/spotlight_search.js";
import "#vendor/fuse.js/dist/fuse.basic.min.js";
import "./autocomplete/spotlight.js";
import "./autocomplete/result_list.js";
import "./autocomplete/result.js";
import "./autocomplete/inflector.js";
import debounce from "lodash/debounce";
import "#legacy/utilities/regexp.js";
import "#legacy/embed/utilities/url_modifier.js";

// Snapshot any Autocomplete sub-classes (Spotlight, ResultList, Result, Inflector)
// that the `./autocomplete/*.js` imports above attached to
// `DoctorFinder.Views.Autocomplete`. Under Vite the hoisted imports run before
// the class assignment below, so without this snapshot + restore the parent
// class definition wipes every child attachment and the autocomplete falls
// back to the base `Sparkle.Spotlight.ItemsList.Item` which renders empty
// `<span class="item-name">` entries instead of the doctor-finder Result
// template. See the namespace-preservation pattern applied to ~12 other files
// last session (wiki: "Vite glob-hoist namespace clobbering").
const __previousAutocomplete = DoctorFinder.Views.Autocomplete;

DoctorFinder.Views.Autocomplete = class Autocomplete extends Sparkle.SpotlightSearch {
  constructor(input, data, options) {
    super(
      null,
      jQuery.extend(
        {
          className: "doctor-finder-autocomplete",
          input: jQuery(input),
          spotlightClass: DoctorFinder.Views.Autocomplete.Spotlight,
          listClass: DoctorFinder.Views.Autocomplete.ResultList,
          itemClass: DoctorFinder.Views.Autocomplete.Result,
          minQueryLength: 0,
          autofocus: true,
          minPhraseLength: 1,
          position: false,
          details: false,
          status: false,
          clearButton: true,
          sortResults(a, b) {
            return (a.matchValue || a.text).length - (b.matchValue || b.text).length;
          }, // Sort query results by text length so any results will be in order of closest match
          highlightMatchingPortions: false,
          suggestedFiltersFallback: false, // If true will fallback to filters suggested by a 3rd party natural language processor e.g. chatterbox

          // Search boosts
          matchingSubdomainBoost: 0.1, // Matches that are on the current website

          // Fuzzy search optimisations
          fuzzyMatchInitialCutoffScore: 0.6, // Discard matches with an initial fuzzy score above this to avoid unnecessary boost calculations / sorting.
          fuzzyMatchFinalCutoffScore: 0.4, // Discard any results over this score after applying boosts

          // Fuzzy search boosts
          fuzzyMatchFirstLetterBoost: 0.1, // Matches that start with the same letter as the search string
          fuzzyMatchOrderedWordBoost: 0.35, // Matches that contain the query words in order

          // Fuzzy search options
          fuzzyMatchExactMatchThreshold: 0.15, // If the top match is the only match below this threshold then treat it as an exact match and only show this result
          fuzzyMatchShowTextSearchFirstScore: 0.3, // If the top match score is over this then display the "query text" at the top
          fuzzyMatchDebug: false,
        },
        options,
      ),
    );
    this.handleInputFocus = this.handleInputFocus.bind(this);
    this.handleInputClick = this.handleInputClick.bind(this);
    this.handleInputChange = this.handleInputChange.bind(this);
    this.setValue = this.setValue.bind(this);
    this.findByValue = this.findByValue.bind(this);
    this.setData = this.setData.bind(this);
    this._clearInputValidity = this._clearInputValidity.bind(this);
    this._fetch = this._fetch.bind(this);
    this._renderResultsAndFocusLast = this._renderResultsAndFocusLast.bind(this);
    this.shouldFetchSuggestedFilters = this.shouldFetchSuggestedFilters.bind(this);
    this.canAddSuggestedFilters = this.canAddSuggestedFilters.bind(this);
    this.filterCollection = this.filterCollection.bind(this);
    this.filterByExactMatch = this.filterByExactMatch.bind(this);
    this.filterByFuzzyMatch = this.filterByFuzzyMatch.bind(this);
    this.fuzzyMatchOrderedWordBoostRegex = this.fuzzyMatchOrderedWordBoostRegex.bind(this);
    this.addTextSearchOption = this.addTextSearchOption.bind(this);
    this.addNoResultsTextOption = this.addNoResultsTextOption.bind(this);
    this.buildTextSearchOption = this.buildTextSearchOption.bind(this);
    this.addSuggestedFilters = this.addSuggestedFilters.bind(this);
    this.buildSuggestedFilters = this.buildSuggestedFilters.bind(this);
    this.chatterBox = this.chatterBox.bind(this);
    if (options == null) {
      options = {};
    }

    this.input = this.options.input;
    this.current_website = Sparkle.Embed.Utilities.detectCurrentWebsite();

    // Fuse.js scoring (0 is a perfect match, 1 is a complete mismatch) - https://www.fusejs.io/concepts/scoring-theory.html
    this.fuse = new Fuse([], {
      keys: ["matchValue"],
      includeScore: true,
      includeMatches: true,
      ignoreLocation: true, // setting this will ignore the distance, threshold and location values
      minMatchCharLength: 2,
      shouldSort: false,
    }); // we apply our own sorting after applying boosts to the matches

    this.setData(data);

    // BEHAVIOUR

    this.input.on("focus", this.handleInputFocus);
    this.input.on("click", this.handleInputClick);
    this.input.on("input", this.handleInputChange);
  }

  // EVENT HANDLERS

  handleInputFocus(event) {
    return typeof event.currentTarget?.select === "function" ? event.currentTarget.select() : undefined;
  }

  handleInputClick() {
    this._clearInputValidity(true);
    return this.updateResults();
  }

  handleInputChange() {
    return this._clearInputValidity();
  }

  // API

  // Find the first item with a matching value and set the input text to that value
  setValue(selectedValue) {
    let text;
    if (selectedValue) {
      const datum = this.findByValue(selectedValue);
      if (datum != null) {
        ({ text } = datum);
      }
    }

    return this.setInputValue(text);
  }

  findByValue(selectedValue) {
    return (
      this.noQueryData.find((datum) => datum.value === selectedValue) ||
      this.queryData.find((datum) => datum.value === selectedValue)
    );
  }

  // Generate two sets of data, one for when a query is present, one for when there is none
  setData(data) {
    if (!jQuery.isArray(data)) {
      data = jQuery.map(data, (datum) => datum);
    } // Coerce { locations: [...], categories: [...] } to an array
    this.noQueryData = jQuery.map(data, (datum) => {
      if (datum.visibility !== "query") {
        return datum;
      }
    }); // TODO: Ensure selected value is visible
    this.queryData = jQuery.map(data, (datum) => {
      if (datum.visibility !== "no-query") {
        return datum;
      }
    }); // TODO: Ensure selected value is visible
    if (this.options.sortResults) {
      this.queryData = this.queryData.sort(this.options.sortResults);
    }
    this.queryData.forEach((datum) => datum.matchValue || (datum.matchValue = datum.text)); // Simplify matching by ensuring the matchValue is always set (we don't transmit it set when it matches the `text` value in order to reduce transmitted payload)

    if (this.options.transliterateData) {
      this.queryData.forEach(
        (datum) => (datum.matchValue = DoctorFinder.Views.Autocomplete.Inflector.transliterate(datum.matchValue)),
      );
    }

    this.spotlight.setItems(this.noQueryData);
    this.fuse.setCollection(this.queryData);

    // We may not have shown them the first time if there were none available so attempt to show them now that we have some
    if (this.options.alwaysShowResults) {
      return this.showResults();
    }
  }

  // PRIVATE

  _clearInputValidity(report = false) {
    if (!(this.input[0] != null ? this.input[0].setCustomValidity : undefined)) {
      return;
    }

    this.input[0].setCustomValidity("");
    if (report) {
      return typeof this.input[0].reportValidity === "function" ? this.input[0].reportValidity() : undefined;
    }
  }

  _fetch() {
    if (!this.debouncedSuggestedFiltersFetch) {
      this.debouncedSuggestedFiltersFetch = debounce(
        (results) =>
          this.addSuggestedFilters(results).then(() => {
            if (this.canAddSuggestedFilters()) {
              return this._renderResultsAndFocusLast(results);
            }
          }),
        500,
      );
    }

    if (!this.debouncedFetch) {
      this.debouncedFetch = debounce(() => {
        const [results, bestMatchScore] = Array.from(this.filterCollection());
        if (this.shouldFetchSuggestedFilters(results)) {
          this.debouncedSuggestedFiltersFetch(results);
        }
        if (this.options.showTextSearchOptionAtCutoff && this.getQuery()) {
          this.addTextSearchOption(results, bestMatchScore);
        }
        if (this.getQuery().length && !results.length) {
          this.addNoResultsTextOption(results);
        }
        return this._renderResults(results);
      }, 100);
    }

    return this.debouncedFetch(); // Fuzzy matching can be slow with a large data set, we don't want to unnecessarily search if the query changes very rapidly
  }

  _renderResultsAndFocusLast(results) {
    this._renderResults(results);
    return this.spotlight.focusLast();
  }

  shouldFetchSuggestedFilters(results) {
    return this.canAddSuggestedFilters() && !results.length; // Only add suggested filters when no other results found
  }

  canAddSuggestedFilters() {
    if (!this.chatterBox) {
      return false;
    } // Is chatterBox setup?
    if (!this.getQuery().length) {
      return false;
    } // Is there something to search for?
    return true;
  }

  filterCollection() {
    let bestMatchScore;
    let results = [];
    if (!this.queryData.length) {
      return [results];
    } // Bail out before recording state if we don't have any data yet

    let query = this.getQuery();
    if (this.options.transliterate) {
      query = DoctorFinder.Views.Autocomplete.Inflector.transliterate(query);
    }

    if (query) {
      [results, bestMatchScore] = Array.from(this.filterByExactMatch(query));
      if (!results.length) {
        [results, bestMatchScore] = Array.from(this.filterByFuzzyMatch(query));
      }
    } else {
      results = this.noQueryData;
    }

    if (query) {
      this.lastResults = results;
    }

    return [results, bestMatchScore];
  }

  filterByExactMatch(query) {
    let bestMatchScore;
    const query_words = jQuery.map(query.split(" "), (word) => "\\b" + Sparkle.Utilities.escapeForRegExp(word));
    const pattern = new RegExp(query_words.join(".+"), "i");

    let results = [];
    this.queryData.forEach((datum) => {
      if (results.length === this.options.limit) {
        return false;
      }
      if (pattern.test(datum.matchValue)) {
        datum.score = 0;
        // Boosts a record whose CANONICAL (home) site is the current website. Every client that renders this
        // Sparkle finder populates canonical_website only (allowed_websites NULL), so home-site == presence for
        // them. A future multi-site finder client would want presence membership (allowed_websites) here so a
        // record present on the current site — but homed elsewhere — is also boosted. See
        // https://github.com/combinaut/sparkle-ucsf/issues/477.
        if (this.options.matchingSubdomainBoost && this.current_website === datum.canonical_website) {
          datum.score -= 0.1;
        }
        return results.push(datum);
      }
    });

    results = results.sort((a, b) => a.score - b.score);

    if (results.length) {
      bestMatchScore = results[0].score;
    }

    return [results, bestMatchScore];
  }

  filterByFuzzyMatch(query) {
    // things that will affect the sort order
    let bestMatchScore;
    const fuzzyOrderedWordRegex = this.fuzzyMatchOrderedWordBoostRegex(query);
    const firstLetterRegex = new RegExp("^" + query[0].toLowerCase(), "i");
    let results = this.fuse.search(query);

    // if @options.fuzzyMatchDebug
    //   console.debug("filterByFuzzyMatch: " + query)
    //   console.debug(fuzzyOrderedWordRegex)

    results = results.filter((result) => result.score < this.options.fuzzyMatchInitialCutoffScore);

    results.forEach((result) => {
      // if @options.fuzzyMatchDebug
      //   # console.log(result)
      //   # visualise how the fuzzy match was made - query:"Bob Jon" will match item:"Bob Jones" printing:"Bob Jon.."
      //   matchIndex = result.item.matchValue.replace(/[a-zA-Z0-9]/g, '.').split('');

      //   result.matches[0].indices.forEach (index) =>
      //     for i in [index[0]...index[1]]
      //       matchIndex[i] = result.item.matchValue[i]

      if (firstLetterRegex.test(result.item.matchValue)) {
        result.score -= this.options.fuzzyMatchFirstLetterBoost;
      }
      // console.debug(result.item.matchValue + " firstLetterMatch:" + result.score) if @options.fuzzyMatchDebug
      if (fuzzyOrderedWordRegex.test(result.item.matchValue)) {
        result.score -= this.options.fuzzyMatchOrderedWordBoost;
      }
      // console.debug(result.item.matchValue + " orderedWordBoost:" + result.score) if @options.fuzzyMatchDebug

      if (
        this.options.matchingSubdomainBoost &&
        result.item.canonical_website !== undefined &&
        this.current_website === result.item.canonical_website
      ) {
        return (result.score -= this.options.matchingSubdomainBoost);
      }
    });
    // console.debug(result.item.matchValue + " subdomainBoost:" + result.score + " :" + result.item.canonical_website + ":" + @current_website) if @options.fuzzyMatchDebug

    results = results.filter((result) => result.score < this.options.fuzzyMatchFinalCutoffScore);

    results = results.sort((a, b) => a.score - b.score);

    // if there is only one result which has a score below the @options.fuzzyMatchExactMatchThreshold then treat it as an exact match
    if (
      results.slice(0, 2).filter((result) => result.score < this.options.fuzzyMatchExactMatchThreshold).length === 1
    ) {
      results = [results[0]];
    }

    if (results.length) {
      bestMatchScore = results[0].score;
    }
    results = results.map((o) => o["item"]);

    return [results, bestMatchScore];
  }

  // The goal of this is to boost the score of items returned by a fuzzy search when the words appear in the desired order but may contain other characters / words between
  // E.g. when searching for "Brian Jones" this will construct a regex of "\bbrian\b.+\bjone" which will match and boost "Brian W. Jones"
  //      a search for "physicial rehab" will construct a regex of "\bphysical\b.+\brehab" which will match and boost "Physical Medicine and Rehabilitation".
  fuzzyMatchOrderedWordBoostRegex(query) {
    const words = query.split(/\s+/);
    const query_words = jQuery.map(
      words.slice(0, -1),
      (word) => "\\b" + Sparkle.Utilities.escapeForRegExp(word) + "\\b",
    );
    query_words.push("\\b" + Sparkle.Utilities.escapeForRegExp(words[words.length - 1]));
    return new RegExp(query_words.join(".+"), "i");
  }

  addTextSearchOption(results, bestMatchScore) {
    if (bestMatchScore < this.options.fuzzyMatchShowTextSearchFirstScore) {
      return results.push(this.buildTextSearchOption());
    } else {
      return results.unshift(this.buildTextSearchOption());
    }
  }

  addNoResultsTextOption(results) {
    return results.push({
      text: `We're sorry, we found no results for '${this.getQuery()}'. Please try again.`,
      clear: true,
    });
  }

  buildTextSearchOption() {
    const query = this.getQuery();
    if (typeof this.options.showTextSearchOptionAtCutoff === "function") {
      return this.options.showTextSearchOptionAtCutoff(query);
    } else {
      return { text: `\"${query}\"`, query, group: "Search for phrase" };
    }
  }

  addSuggestedFilters(results) {
    const query = this.getQuery();
    return this.buildSuggestedFilters(query).then((suggestedFilter) => {
      results.push(suggestedFilter);
      return results;
    });
  }

  buildSuggestedFilters(query) {
    const deferred = jQuery.Deferred();

    if (this.chatterBox()) {
      this.chatterBox()
        .fetchChatterBoxData(query)
        .then((data) => {
          const filterText = [];
          const filters = {};
          jQuery(document).trigger("autocomplete.sparkle", ["chatterbox", query, data]);

          for (var filter in data) {
            var filterData = data[filter];
            if (filterData.text.length) {
              filterText.push(filterData.text);
              filters[filter] = filterData.value;
            }
          }

          const result = {
            filters,
            type: "suggested-filter",
            showFilters: filterText,
            group: "Search with suggested filters",
            navigateOnSelect: true,
          };

          if (filterText.length && query === this.getQuery()) {
            // check input hasn't changed in the meantime
            return deferred.resolve(result);
          } else {
            return deferred.fail();
          }
        });
    }

    return deferred.promise();
  }

  chatterBox() {
    if (!this.options.suggestedFiltersFallback) {
      return false;
    } // Is feature enabled?
    return (
      this.chatterBoxFetcher || (this.chatterBoxFetcher = new DoctorFinder.Helpers.AutocompleteChatterBoxFetcher())
    );
  }
};

// Restore the sub-classes (Spotlight, ResultList, Result, Inflector) attached
// by the `./autocomplete/*.js` imports above so
// `new DoctorFinder.Views.Autocomplete(...)` can resolve
// `options.spotlightClass` / `listClass` / `itemClass` via
// `DoctorFinder.Views.Autocomplete.Spotlight`, etc. See the matching comment
// at the top of this file.
if (__previousAutocomplete) {
  Object.assign(DoctorFinder.Views.Autocomplete, __previousAutocomplete);
}
